import {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useTranslation } from 'react-i18next';
import { SportEvent } from 'videoservice-openapi-react';
import { useEventsFilter, useMapEvents } from 'features/event';
import { EventDate } from 'entities/event';
import { EElementSize } from 'shared/types';
import {
  Button,
  Checkbox,
  DropContent,
  DropFooter,
  DropHeader,
  EButtonType,
  FieldText,
  SearchIcon,
} from 'shared/ui';
import { ECheckboxPosition } from 'shared/ui/checkbox/checkbox.types';
import { useDropDownContext } from 'shared/ui/drop-down';
import { EActionsAlign } from 'shared/ui/drop-footer';
import { LoadingIndicator } from 'shared/ui/loading-indicator';
import {
  BindModalContentStyled,
  LoaderWrapper,
  NotFoundContent,
  SelectableEventItemStyled,
  SelectAllStyled,
} from './bind-modal-content.styled';
import {
  IBindModalContentProps,
  ISelectableEventItemProps,
  ISelectableEventListProps,
} from './bind-modal-content.types';

const SelectableEventItem: FC<ISelectableEventItemProps> = ({
  item,
  selected,
  isSelectable = true,
  onClick,
}) => {
  const isChecked = useMemo(() => selected.includes(item.id), [selected, item]);

  const itemClickHandler = (e: MouseEvent) => {
    e.preventDefault();

    if (!isSelectable) {
      return;
    }

    onClick(item);
  };

  return (
    <SelectableEventItemStyled
      $isSelectable={isSelectable}
      $isChecked={isChecked}
      onClick={itemClickHandler}
    >
      <div className="status">
        {item.live && <span className="live-status"></span>}
      </div>
      <div>
        <EventDate date={item.date} />
        {item.description} [{item.id}]
      </div>
      {isSelectable && <Checkbox checked={isChecked} readOnly />}
    </SelectableEventItemStyled>
  );
};

const SelectableEventList: FC<ISelectableEventListProps> = ({
  list,
  selected,
  disabledEvents = [],
  onCheck,
}) => {
  const itemClickHandler = useCallback(
    (event: SportEvent) => {
      const newSelected = selected.includes(event.id)
        ? selected.filter(el => el !== event.id)
        : [...selected, event.id];

      onCheck(newSelected);
    },
    [selected, onCheck],
  );

  return (
    <Scrollbars autoHeightMax={380} autoHeight>
      {list.map(event => (
        <SelectableEventItem
          selected={selected}
          isSelectable={!disabledEvents.includes(event.id)}
          key={event.id}
          item={event}
          onClick={() => itemClickHandler(event)}
        />
      ))}
    </Scrollbars>
  );
};

export const BindModalContent: FC<IBindModalContentProps> = ({
  broadcast,
  onSubmit,
}) => {
  const { closeDropdown } = useDropDownContext();
  const { t } = useTranslation();
  const [selectedEvents, setSelectedEvents] = useState<number[]>([]);

  const alreadyMappedEvents = useMemo(
    () => broadcast.mappings?.map(el => el.sbSportEventId) ?? [],
    [broadcast.mappings],
  );

  const sportIds = useMemo(
    () => (broadcast.sportId ? [broadcast.sportId] : []),
    [broadcast.sportId],
  );

  const {
    searchValue,
    setSearchValue,
    eventList,
    isLoading,
    isNotFound,
    isIdle,
  } = useEventsFilter({
    sportIds,
  });

  useEffect(() => {
    setSelectedEvents([]);
  }, [searchValue]);

  const onBindSuccess = useCallback(() => {
    closeDropdown();
  }, [closeDropdown]);

  const { mapSelectedEvents, mapEventMutation } = useMapEvents({
    broadcast,
    eventList,
    onSuccess: onBindSuccess,
  });

  const newSelectedEvents = useMemo(
    () => selectedEvents.filter(el => !alreadyMappedEvents.includes(el)),
    [alreadyMappedEvents, selectedEvents],
  );

  const availableEvents = useMemo(
    () =>
      eventList
        .filter(el => !alreadyMappedEvents.includes(el.id))
        .map(el => el.id),
    [alreadyMappedEvents, eventList],
  );

  const canMap = useMemo(() => {
    return newSelectedEvents.length !== 0 && !mapEventMutation.isPending;
  }, [mapEventMutation.isPending, newSelectedEvents.length]);

  const bindClickHandler = useCallback(() => {
    try {
      mapSelectedEvents(newSelectedEvents);
    } catch (error) {
      console.error(error);
    } finally {
      if (onSubmit && typeof onSubmit === 'function') {
        onSubmit(newSelectedEvents);
      }
    }
  }, [mapSelectedEvents, newSelectedEvents, onSubmit]);

  const selectAllClick = useCallback(() => {
    setSelectedEvents(
      selectedEvents.length === availableEvents.length ? [] : availableEvents,
    );
  }, [availableEvents, selectedEvents.length]);

  const isSelectAllDisabled = useMemo(
    () => availableEvents.length === 0,
    [availableEvents.length],
  );

  const isSelectedAll = useMemo(() => {
    return newSelectedEvents.length === availableEvents.length
      ? true
      : newSelectedEvents.length === 0
        ? false
        : undefined;
  }, [availableEvents.length, newSelectedEvents.length]);

  return (
    <DropContent>
      <BindModalContentStyled>
        <DropHeader>
          <FieldText
            autoFocus
            icon={<SearchIcon />}
            $size={EElementSize.M}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
          />
        </DropHeader>

        {isLoading ? (
          <LoaderWrapper>
            <LoadingIndicator $small />
          </LoaderWrapper>
        ) : isNotFound ? (
          <NotFoundContent>
            Поиск не дал результата. Измените запрос
          </NotFoundContent>
        ) : isIdle ? (
          <NotFoundContent>
            Введите запрос в поисковую строку, чтобы начать поиск
          </NotFoundContent>
        ) : (
          <>
            {eventList.length > 0 && (
              <SelectAllStyled>
                <Checkbox
                  position={ECheckboxPosition.RIGHT}
                  disabled={isSelectAllDisabled}
                  readOnly
                  onClick={selectAllClick}
                  checked={isSelectedAll}
                >
                  Выбрать все
                </Checkbox>
              </SelectAllStyled>
            )}
            <SelectableEventList
              list={eventList}
              onCheck={setSelectedEvents}
              selected={selectedEvents}
              disabledEvents={alreadyMappedEvents}
            />
          </>
        )}

        <DropFooter actionsAlign={EActionsAlign.START}>
          <Button
            isLoading={mapEventMutation.isPending}
            $size={EElementSize.M}
            onClick={bindClickHandler}
            disabled={!canMap}
          >
            {t('bind')}
          </Button>
          <Button
            $type={EButtonType.SECONDARY}
            $size={EElementSize.M}
            onClick={() => closeDropdown()}
          >
            {t('cancel')}
          </Button>
        </DropFooter>
      </BindModalContentStyled>
    </DropContent>
  );
};
